import {
  FormLabel,
  TextField,
  TextFieldVariants,
  InputAdornment,
  Typography,
  Button,
  Grid,
} from "@mui/material";
import { basicTextFieldValidator, inputStateUpdate } from "../utility";
import { useEffect, useState } from "react";
import { AddMemberFormDataUpdated } from "../AddMemberForm";
import { AddMerchantFormType } from "../AddMerchant";
import { useTheme } from "@mui/material/styles";
import useFormStyles from "../../../styles/useFormStyles";
import { ReactElement } from "react";

export type BasicTextFieldProps = {
  stateKey: string;
  setState: Function;
  variant?: TextFieldVariants;
  label?: string;
  align?: string;
  adornment?: ReactElement;
  placeholder: string;
  id?: string;
  type?: string;
  defaultValue?: string;
  required?: boolean;
  matcherValue?: string;
  disableSubmit?: Function;
  clear?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  loyaltyPointsValue?: number;
  selectType?: string | String;
  value?: string | undefined;
  existingValues?: string[];
  multiline?: boolean;
  rows?: number;
  errorMessage?: string;
  applyStrictMobileValidation?: boolean;
  isSingapore?: boolean;
  helperTextInfo?: string;
  allowZeroValue?: boolean;
  storeCountryCode?: string;
  countryOfResidence?: string;
};

export type FormErrorType = {
  error: boolean;
  message?: string;
};

const BasicTextField = (props: BasicTextFieldProps) => {
  const {
    label,
    align,
    variant,
    adornment,
    stateKey,
    placeholder,
    setState,
    id,
    type,
    defaultValue,
    required,
    matcherValue,
    disableSubmit,
    disabled,
    clear,
    readonly,
    onKeyDown,
    loyaltyPointsValue,
    selectType,
    existingValues,
    multiline,
    rows,
    errorMessage,
    applyStrictMobileValidation,
    isSingapore,
    helperTextInfo,
    allowZeroValue,
    storeCountryCode,
    countryOfResidence,
  } = props;
  const [error, setError] = useState<FormErrorType>({
    error: false,
    message: "",
  });
  const [textFieldValue, setTextFieldValue] = useState(
    defaultValue ? defaultValue : "",
  );
  const [visited, setVisited] = useState(false);
  const formStyles = useFormStyles();
  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTextFieldValue(e.target.value);
  };
  const theme = useTheme();
  const readOnlyStyles: React.CSSProperties = {
    backgroundColor: theme.color?.readOnly,
  };

  const validate = () => {
    const shouldValidate =
      visited ||
      (Boolean(applyStrictMobileValidation) && textFieldValue.length > 0);

    basicTextFieldValidator(
      stateKey!,
      textFieldValue,
      setTextFieldValue,
      setError,
      disableSubmit!,
      shouldValidate,
      matcherValue,
      loyaltyPointsValue,
      selectType,
      existingValues,
      applyStrictMobileValidation,
      isSingapore,
      allowZeroValue,
    );
  };

  useEffect(() => {
    if (defaultValue) setTextFieldValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    inputStateUpdate(stateKey, setState, textFieldValue);
    if (disableSubmit instanceof Function) {
      validate();
    }
    if (clear) {
      setTextFieldValue("");
      setVisited(false);
    }
  }, [
    textFieldValue,
    visited,
    clear,
    isSingapore,
    applyStrictMobileValidation,
    storeCountryCode,
    countryOfResidence,
  ]);

  return (
    <>
      {label ? (
        <FormLabel
          sx={{
            ...formStyles.formLabel,
            textAlign: align ?? "left",
          }}
        >
          {label}
        </FormLabel>
      ) : null}
      <TextField
        multiline={multiline}
        rows={rows}
        required={required ? required : false}
        disabled={disabled}
        style={readonly ? readOnlyStyles : {}}
        inputProps={{
          autoComplete: "off",
        }}
        helperText={
          errorMessage || (error.error && error.message) ? (
            <Typography
              component={"span"}
              textAlign={"left"}
              color={"error"}
              fontSize={"0.7rem"}
              sx={{ mx: 2, mt: 0.5, position: "absolute", top: "100%" }}
              mt={0}
            >
              {errorMessage || error.message}
            </Typography>
          ) : helperTextInfo ? (
            <Typography
              component={"span"}
              textAlign={"left"}
              color={"text.secondary"}
              fontSize={"0.7rem"}
              sx={{ mx: 2, mt: 0.5, position: "absolute", top: "100%" }}
              mt={0}
            >
              {helperTextInfo}
            </Typography>
          ) : null
        }
        InputProps={
          adornment
            ? {
                endAdornment: (
                  <InputAdornment sx={{ height: "100%" }} position={"start"}>
                    <Grid
                      display={"flex"}
                      justifyContent={"center"}
                      alignItems={"center"}
                      sx={{ maxHeight: "100%" }}
                    >
                      {adornment}
                    </Grid>
                  </InputAdornment>
                ),
                readOnly: readonly,
              }
            : { readOnly: readonly }
        }
        onChange={changeHandler}
        onFocus={() => setVisited(true)}
        sx={{
          p: 0,
          fontFamily: theme.font?.fontFamily.primary,
        }}
        error={error.error}
        type={type}
        id={id}
        name={id}
        fullWidth
        label=""
        placeholder={placeholder}
        variant={variant}
        size={"small"}
        value={textFieldValue}
        autoComplete="off"
        onKeyDown={onKeyDown}
      />
    </>
  );
};

export default BasicTextField;
